import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getAllParticipantsRegistrationStatus,
  getEvent,
} from "../../services/EventService";
import { AxiosResponse } from "axios";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Button, Input, InputRef, Space, Spin, Table } from "antd";
import { ParticipantDto } from "../../models/Participant";
import { useApplicationContext } from "../../application/ApplicationContext";
import {
  HomeOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { FilterConfirmProps } from "antd/es/table/interface";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { RegistrationDto, RegistrationStatus } from "../../models/Registration";
import "./EventOverviewPage.scss";

const ViewParticipant = () => {
  const { id } = useParams();
  const { registrationStatus } = useParams();
  const [allParticipant, setAllParticipant] = useState<ParticipantDto[]>([]);
  const { setBreadcrumb } = useApplicationContext();
  const searchInput = useRef<InputRef>(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [activeSpinner, setActiveSpinner] = useState(false);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);

  useEffect(() => {
    setActiveSpinner(true);
    getEvent(id as string).then((res: AxiosResponse) => {
      let data = res.data.result;
      setBreadcrumb(["Događaj", data.name]);
    });
    getAllParticipantsRegistrationStatus(
      id as string,
      registrationStatus as string
    ).then((res: AxiosResponse) => {
      let data = [...res.data.result];
      let newd = data.map((el: any) => {
        return {
          ...el,
          companyName:
            el.registrationCompanyName == null
              ? "/"
              : el.registrationCompanyName,
        };
      });
      setAllParticipant(newd);
      setActiveSpinner(false);
    });
  }, []);

  type DataIndex = keyof RegistrationDto;

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<RegistrationDto> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (value, record: any) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const columns: ColumnsType<RegistrationDto | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Ime",
      dataIndex: "firstName",
      key: "firstName",
      sorter: (a, b) => (a.firstName < b.firstName ? -1 : 1),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps("firstName"),
      render: (firstName, record) => {
        return (
          <span className="blue-text name-container">
            <span
              className={
                record?.status === RegistrationStatus.Pending
                  ? "status-dot status-dot-pending"
                  : record?.status === RegistrationStatus.Rejected
                  ? "status-dot status-dot-rejected"
                  : record?.status === RegistrationStatus.Approved
                  ? "status-dot status-dot-approved"
                  : record?.status === RegistrationStatus.Reserved
                  ? "status-dot status-dot-reserved"
                  : record?.status === RegistrationStatus.Paid
                  ? "status-dot status-dot-paid"
                  : ""
              }
            ></span>
            {firstName}
          </span>
        );
      },
    },
    {
      title: "Prezime",
      dataIndex: "lastName",
      key: "lastName",
      sorter: (a, b) => (a.lastName < b.lastName ? -1 : 1),
      render: (lastName) => <span className="blue-text">{lastName}</span>,
      ...getColumnSearchProps("lastName"),
    },
    {
      title: "Pravno lice",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a, b) =>
        (a?.companyName ?? "") < (b?.companyName ?? "") ? -1 : 1,
      render: (companyName) => <span className="blue-text">{companyName}</span>,
      //...getColumnSearchProps("registrationCompanyName"),
    },

    {
      title: "Kontakt telefon",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => <span className="blue-text">{phone}</span>,
      ...getColumnSearchProps("phone"),
    },
    {
      title: "E-mail",
      dataIndex: "email",
      key: "email",
      render: (email) => <span className="blue-text">{email}</span>,
      ...getColumnSearchProps("email"),
    },
  ];

  const numberStatus = () => {
    switch (registrationStatus) {
      case "Učesnik":
        return (
          <div className="div-icon">
            <div className="icon">
              <TeamOutlined />
            </div>
            <div className="text-i">
              <div>
                <p>Ukupno učesnika</p>
              </div>
              <div>
                <p>{allParticipant.length}</p>
              </div>
            </div>
          </div>
        );
      case "Predavač":
        return (
          <div className="div-icon">
            <div className="icon">
              <UserSwitchOutlined />
            </div>
            <div className="text-i">
              <div>
                <p>Ukupno predavača</p>
              </div>
              <div>
                <p>{allParticipant.length}</p>
              </div>
            </div>
          </div>
        );
      case "Gost":
        return (
          <div className="div-icon">
            <div className="icon">
              <TeamOutlined />
            </div>
            <div className="text-i">
              <div>
                <p>Ukupno gostiju</p>
              </div>
              <div>
                <p>{allParticipant.length}</p>
              </div>
            </div>
          </div>
        );
      case "pravnaLica":
        return (
          <div className="div-icon">
            <div className="icon">
              <HomeOutlined />
            </div>
            <div className="text-i">
              <div>
                <p>Ukupno pravnih lica</p>
              </div>
              <div>
                <p>{allParticipant.length}</p>
              </div>
            </div>
          </div>
        );
      case "fizickaLica":
        return (
          <div className="div-icon">
            <div className="icon">
              <UserOutlined />
            </div>
            <div className="text-i">
              <div>
                <p>Ukupno fizičkih lica</p>
              </div>
              <div>
                <p>{allParticipant.length}</p>
              </div>
            </div>
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <PageLayout>
      <Spin spinning={activeSpinner}>
        <div className="header-p">
          <div className="title-p-o">
            <p>Opšte informacije o događaju</p>
          </div>
          <div className="icon-div">{numberStatus()}</div>
        </div>

        <Table
          sticky={true}
          scroll={{ x: 1300 }}
          columns={columns}
          dataSource={allParticipant}
          rowKey={(rowData: any) => rowData.id}
          pagination={{
            onChange(current) {
              setPageNumber(current);
            },
            defaultPageSize: 30,
            showSizeChanger: true,
            pageSizeOptions: ["30", "50", "100"],
          }}
          onChange={(pagination, filters, sorter, extra) => {
            setPaginationNumber(pagination.pageSize);
          }}
          className="table-container"
        />
      </Spin>
    </PageLayout>
  );
};

export default ViewParticipant;
